import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Supercluster from "supercluster";
import { supabase } from "../lib/supabase";
import { ensureOperatorsSuccess } from "../lib/operatorsApi";
import { useSearchStore } from "../stores/searchStore";
import { useAuthStore } from "../stores/authStore";
import type { OperatorWithCoords } from "../types/database";
import { getCacheData, setCacheData, generateCacheKey, getTotalOperatorsCount } from "../lib/operatorsCache";

// GeoJSON Feature type for operators
export interface OperatorFeature {
  type: "Feature";
  properties: OperatorWithCoords & { cluster?: false };
  geometry: {
    type: "Point";
    coordinates: [number, number]; // [lng, lat]
  };
}

// Cluster feature from Supercluster
export interface ClusterFeature {
  type: "Feature";
  properties: {
    cluster: true;
    cluster_id: number;
    point_count: number;
    point_count_abbreviated: string | number;
    // Typology tracking: if all points have same typology, this is that typology; if mixed, it's null
    uniformTypology?: string | null;
    // Macro category tracking: if all points have same macro_category, this is that category; if mixed, it's null
    uniformMacroCategory?: string | null;
  };
  geometry: {
    type: "Point";
    coordinates: [number, number]; // [lng, lat]
  };
}

export type MapFeature = OperatorFeature | ClusterFeature;

// Check if feature is a cluster
export function isCluster(feature: MapFeature): feature is ClusterFeature {
  return feature.properties.cluster === true;
}

// Custom cluster properties for typology and category tracking (only custom fields - Supercluster adds cluster/cluster_id/point_count automatically)
export interface CustomClusterProps {
  // Typology tracking: if all points have same typology, this is that typology; if mixed, it's null
  uniformTypology: string | null;
  // Macro category tracking: if all points have same macro_category, this is that category; if mixed, it's null
  uniformMacroCategory: string | null;
}

// Full cluster properties (built-in + custom)
export interface ClusterProperties extends CustomClusterProps {
  cluster: true;
  cluster_id: number;
  point_count: number;
  point_count_abbreviated: string | number;
}

// Supercluster options with map/reduce to track typology in clusters
// Optimized for large datasets (86k+ operators)
const SUPERCLUSTER_OPTIONS: Supercluster.Options<
  OperatorWithCoords,
  CustomClusterProps
> = {
  radius: 100, // Slightly larger clusters to reduce markers rendered
  maxZoom: 18, // Keep clustering even at very high zoom levels
  minZoom: 0,
  minPoints: 2,
  extent: 512,
  nodeSize: 96, // Fewer tree levels, faster getClusters on dense data
  // Map each point to extract its typology and macro category
  map: (props) => ({
    uniformTypology: props.typology || null,
    uniformMacroCategory: props.macro_category || null,
  }),
  // Reduce: combine typologies - if all same keep it, if different set to null (mixed)
  reduce: (accumulated, props) => {
    // Typology tracking
    if (accumulated.uniformTypology !== null) {
      if (accumulated.uniformTypology !== props.uniformTypology) {
        accumulated.uniformTypology = null;
      }
    }
    // Macro category tracking
    if (accumulated.uniformMacroCategory !== null) {
      if (accumulated.uniformMacroCategory !== props.uniformMacroCategory) {
        accumulated.uniformMacroCategory = null;
      }
    }
  },
};

// Page size for progressive loading (loads operators in batches)
const PAGE_SIZE = 30000;

// Cache key prefix
const CACHE_KEY_PREFIX = "all-operators";

/**
 * Protected API call to operators-query Edge Function
 */
async function queryOperatorsProtected(
  token: string,
  action: string,
  params: Record<string, unknown> = {}
): Promise<OperatorWithCoords[]> {
  const { data, error } = await supabase.functions.invoke("operators-query", {
    body: { action, params },
    headers: {
      "X-Portale-Token": token,
    },
  });

  return ensureOperatorsSuccess<OperatorWithCoords[]>(data, error);
}

/**
 * Convert operators to GeoJSON features for Supercluster
 */
function operatorsToFeatures(operators: OperatorWithCoords[]): OperatorFeature[] {
  return operators.map((op) => ({
    type: "Feature" as const,
    properties: { ...op, cluster: false as const },
    geometry: {
      type: "Point" as const,
      coordinates: [op.lng, op.lat] as [number, number],
    },
  }));
}

/**
 * Hook to get clusters for the current map viewport
 * Uses Supercluster for efficient clustering of large datasets
 * PROTECTED by authentication - loads progressively with caching
 */
export function useSupercluster() {
  const queryClient = useQueryClient();
  const { filters, mapBounds, zoomLevel, drawnPolygon, listMode, setResults, setTotalInView, setFilteredCount } = useSearchStore();
  const { token, isAuthenticated } = useAuthStore();

  // State for progressive loading
  const [allOperators, setAllOperators] = useState<OperatorWithCoords[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // Track current fetch to avoid duplicates
  const fetchIdRef = useRef(0);
  const clusterRef = useRef<Supercluster<OperatorWithCoords, Supercluster.AnyProps> | null>(null);

  // Create cache key for this filter combination
  const cacheKey = useMemo(() => {
    const normalize = (values?: string[]) => (values || []).slice().sort().join("|") || "all";
    return [
      CACHE_KEY_PREFIX,
      normalize(filters.categories),
      normalize(filters.types),
      filters.dateRange?.start?.toISOString() || "none",
      filters.dateRange?.end?.toISOString() || "none",
    ];
  }, [filters.categories, filters.types, filters.dateRange]);

  // Generate persistent cache key
  const persistentCacheKey = useMemo(() => generateCacheKey({
    categories: filters.categories,
    types: filters.types,
    region: null, // Region filter removed
    dateStart: filters.dateRange?.start?.toISOString().split("T")[0],
    dateEnd: filters.dateRange?.end?.toISOString().split("T")[0],
  }), [filters.categories, filters.types, filters.dateRange]);

  // Progressive fetch with persistent caching (IndexedDB)
  useEffect(() => {
    if (!isAuthenticated || !token) {
      setAllOperators([]);
      return;
    }

    const fetchId = ++fetchIdRef.current;

    // Clear operators immediately when filters change to prevent showing stale data
    console.log(`[useSupercluster] 🔄 Filters changed, clearing operators. New filters:`, {
      categories: filters.categories,
      types: filters.types,
      cacheKey: cacheKey.join('|'),
    });
    setAllOperators([]);

    async function loadProgressively() {
      setIsLoading(true);
      setIsError(false);

      // 1. Check in-memory cache first (React Query)
      const memoryCache = queryClient.getQueryData<OperatorWithCoords[]>(cacheKey);
      if (memoryCache && memoryCache.length > 0) {
        console.log(`[useSupercluster] ✅ Memory cache hit: ${memoryCache.length} operators`);
        // Only set if this fetch is still current
        if (fetchId === fetchIdRef.current) {
          setAllOperators(memoryCache);
        }
        setIsLoading(false);
        return;
      }

      // 2. Check persistent cache (IndexedDB) with count validation
      try {
        console.log(`[useSupercluster] Checking cache with key: ${persistentCacheKey}`);

        // Get current total count for cache validation
        const currentTotalCount = await getTotalOperatorsCount(token!);
        console.log(`[useSupercluster] Current total count from DB: ${currentTotalCount}`);

        const persistentCache = await getCacheData<OperatorWithCoords[]>(
          persistentCacheKey,
          currentTotalCount || undefined
        );

        if (persistentCache && persistentCache.length > 0) {
          console.log(`[useSupercluster] ✅ IndexedDB cache hit: ${persistentCache.length} operators (key: ${persistentCacheKey})`);
          // Only set if this fetch is still current
          if (fetchId === fetchIdRef.current) {
            setAllOperators(persistentCache);
            // Also update memory cache
            queryClient.setQueryData(cacheKey, persistentCache);
          }
          setIsLoading(false);
          return;
        } else {
          console.log(`[useSupercluster] ❌ IndexedDB cache miss or invalidated for key: ${persistentCacheKey}`);
        }
      } catch (e) {
        console.warn('[useSupercluster] IndexedDB check failed:', e);
      }

      // 3. No cache - fetch from API
      console.log(`[useSupercluster] 📡 Fetching from API...`, {
        categories: filters.categories,
        types: filters.types,
      });
      // Clear operators immediately when filters change to avoid showing stale data
      setAllOperators([]);

      let page = 0;
      let hasMore = true;
      let accumulated: OperatorWithCoords[] = [];

      try {
        while (hasMore) {
          if (fetchId !== fetchIdRef.current) return;

          const from = page * PAGE_SIZE;
          const queryParams = {
            offset: from,
            limit: PAGE_SIZE,
            categories: filters.categories || null,
            types: filters.types || null,
            region: null, // Region filter removed
            dateStart: filters.dateRange?.start?.toISOString().split("T")[0] || null,
            dateEnd: filters.dateRange?.end?.toISOString().split("T")[0] || null,
          };
          console.log(`[useSupercluster] Query params:`, queryParams);
          const data = await queryOperatorsProtected(token!, "search_all", queryParams);

          if (!data || data.length === 0) {
            hasMore = false;
            break;
          }

          const transformed = data.map((op) => ({
            ...op,
            lat: Number((op as { lat?: number; latitude?: number }).lat ?? (op as { latitude?: number }).latitude),
            lng: Number((op as { lng?: number; longitude?: number }).lng ?? (op as { longitude?: number }).longitude),
          })) as OperatorWithCoords[];

          accumulated = [...accumulated, ...transformed];

          if (fetchId === fetchIdRef.current) {
            setAllOperators([...accumulated]);
          }

          if (data.length < PAGE_SIZE) {
            hasMore = false;
          }

          page++;
        }

        // Save to both caches when complete
        if (fetchId === fetchIdRef.current && accumulated.length > 0) {
          // Memory cache
          queryClient.setQueryData(cacheKey, accumulated, {
            updatedAt: Date.now(),
          });

          // Get total count for cache invalidation
          const totalCount = await getTotalOperatorsCount(token!).catch(() => null);

          // Persistent cache (IndexedDB) - non-blocking, with total count for invalidation
          setCacheData(persistentCacheKey, accumulated, totalCount || undefined).catch((e) => {
            console.warn('[useSupercluster] Failed to save to IndexedDB:', e);
          });

          console.log(`[useSupercluster] 💾 Cached ${accumulated.length} operators (memory + IndexedDB)${totalCount ? ` with total count: ${totalCount}` : ''}`);
        }
      } catch (err) {
        console.error("Error loading operators:", err);
        if (fetchId === fetchIdRef.current) {
          setIsError(true);
        }
      } finally {
        if (fetchId === fetchIdRef.current) {
          setIsLoading(false);
        }
      }
    }

    loadProgressively();
  }, [token, isAuthenticated, cacheKey, persistentCacheKey, filters, queryClient]);

  // Build Supercluster index when operators change
  const supercluster = useMemo(() => {
    if (allOperators.length === 0) {
      clusterRef.current = null;
      return null;
    }

    const features = operatorsToFeatures(allOperators);
    const cluster = new Supercluster(SUPERCLUSTER_OPTIONS);
    cluster.load(features);
    clusterRef.current = cluster;

    return cluster;
  }, [allOperators]);

  // Filter operators by geographic search (city/region only, exact match)
  const filteredBySearch = useMemo(() => {
    const searchText = filters.searchText?.trim();
    if (!searchText || allOperators.length === 0) {
      return allOperators;
    }
    const searchLower = searchText.toLowerCase();
    return allOperators.filter((op) => {
      const city = op.city?.toLowerCase() || "";
      const region = op.region?.toLowerCase() || "";
      // Exact match for city or region (case-insensitive)
      return city === searchLower || region === searchLower;
    });
  }, [allOperators, filters.searchText]);

  // Filter operators by drawn polygon (client-side)
  const filteredByPolygon = useMemo(() => {
    if (!drawnPolygon || filteredBySearch.length === 0) {
      return filteredBySearch;
    }
    return filteredBySearch.filter((op) => isPointInPolygon([op.lng, op.lat], drawnPolygon));
  }, [filteredBySearch, drawnPolygon]);

  // filteredOperators: operators with all filters applied (categories/types from server, polygon/search from client)
  // Note: allOperators already includes category/type filters applied server-side
  const filteredOperators = useMemo(() => {
    // If we have polygon or search filters, use filteredByPolygon (which already includes allOperators)
    // Otherwise, allOperators already has category/type filters applied server-side
    return filteredByPolygon;
  }, [filteredByPolygon]);

  // If we have filters (polygon or search), create a separate Supercluster
  const filteredSupercluster = useMemo(() => {
    const hasFilters = drawnPolygon || filters.searchText;
    if (!hasFilters || filteredByPolygon.length === 0) {
      return null;
    }
    const features = operatorsToFeatures(filteredByPolygon);
    const cluster = new Supercluster(SUPERCLUSTER_OPTIONS);
    cluster.load(features);
    return cluster;
  }, [drawnPolygon, filters.searchText, filteredByPolygon]);

  // Get clusters for current viewport
  // Performance optimization: limit max markers to prevent freezing
  const MAX_MARKERS = 2500; // Maximum markers to render at once

  const clusters = useMemo((): MapFeature[] => {
    const hasFilters = drawnPolygon || filters.searchText;
    const activeCluster = hasFilters ? filteredSupercluster : supercluster;

    if (!activeCluster || !mapBounds) {
      return [];
    }

    const bbox: [number, number, number, number] = [
      mapBounds.west,
      mapBounds.south,
      mapBounds.east,
      mapBounds.north,
    ];

    try {
      // Use a slightly lower zoom level for clustering to reduce individual points
      // This helps performance when zooming in on dense areas
      const clusterZoom = Math.min(Math.floor(zoomLevel), 17);
      let clustersResult = activeCluster.getClusters(bbox, clusterZoom) as MapFeature[];

      // If we have too many individual points (not clusters), limit them
      const individualPoints = clustersResult.filter(f => !isCluster(f));
      if (individualPoints.length > MAX_MARKERS) {
        // Keep all clusters, but limit individual points
        const clusters = clustersResult.filter(f => isCluster(f));
        const limitedPoints = individualPoints.slice(0, MAX_MARKERS);
        clustersResult = [...clusters, ...limitedPoints];

        console.log(`[useSupercluster] ⚠️ Limiting markers: ${individualPoints.length} → ${limitedPoints.length} individual points`);
      }

      // Overall limit as safety net
      if (clustersResult.length > MAX_MARKERS * 1.5) {
        // Prioritize clusters over individual points
        const clusters = clustersResult.filter(f => isCluster(f));
        const points = clustersResult.filter(f => !isCluster(f));
        const remainingSlots = Math.max(0, MAX_MARKERS * 1.5 - clusters.length);
        clustersResult = [...clusters, ...points.slice(0, remainingSlots)];

        console.log(`[useSupercluster] ⚠️ Overall limit applied: ${clustersResult.length} markers`);
      }

      return clustersResult;
    } catch (e) {
      console.error("Error getting clusters:", e);
      return [];
    }
  }, [supercluster, filteredSupercluster, mapBounds, zoomLevel, drawnPolygon, filters.searchText]);

  // Get operators in viewport
  const operatorsInView = useMemo(() => {
    const hasFilters = drawnPolygon || filters.searchText;
    const baseOperators = hasFilters ? filteredByPolygon : allOperators;

    if (!mapBounds || baseOperators.length === 0) {
      return [];
    }

    return baseOperators.filter((op) =>
      op.lat >= mapBounds.south &&
      op.lat <= mapBounds.north &&
      op.lng >= mapBounds.west &&
      op.lng <= mapBounds.east
    );
  }, [allOperators, mapBounds, drawnPolygon, filters.searchText, filteredByPolygon]);

  // Count individual points in viewport
  const totalInView = useMemo(() => {
    return operatorsInView.length;
  }, [operatorsInView]);

  // Get expansion zoom for a cluster
  const getClusterExpansionZoom = useCallback((clusterId: number): number => {
    const hasFilters = drawnPolygon || filters.searchText;
    const activeCluster = hasFilters ? filteredSupercluster : clusterRef.current;
    if (!activeCluster) return zoomLevel + 2;

    try {
      return activeCluster.getClusterExpansionZoom(clusterId);
    } catch {
      return zoomLevel + 2;
    }
  }, [drawnPolygon, filters.searchText, filteredSupercluster, zoomLevel]);

  // Get leaves (individual points) for a cluster
  const getClusterLeaves = useCallback((clusterId: number, limit = 100): OperatorWithCoords[] => {
    const hasFilters = drawnPolygon || filters.searchText;
    const activeCluster = hasFilters ? filteredSupercluster : clusterRef.current;
    if (!activeCluster) return [];

    try {
      const leaves = activeCluster.getLeaves(clusterId, limit);
      return leaves.map((f) => f.properties as OperatorWithCoords);
    } catch {
      return [];
    }
  }, [drawnPolygon, filters.searchText, filteredSupercluster]);

  // Sync totalInView to store
  useEffect(() => {
    setTotalInView(totalInView, false);
  }, [totalInView, setTotalInView]);

  // Sync filteredCount to store (always the full filtered count, independent of listMode)
  useEffect(() => {
    const hasFilters = drawnPolygon || filters.searchText;
    const count = hasFilters ? filteredByPolygon.length : allOperators.length;
    setFilteredCount(count);
  }, [allOperators, filteredByPolygon, drawnPolygon, filters.searchText, setFilteredCount]);

  // Sync results to store based on listMode
  // "filtered" = all operators with filters applied
  // "visible" = only operators in current map viewport
  useEffect(() => {
    const hasFilters = drawnPolygon || filters.searchText;
    const baseOperators = hasFilters ? filteredByPolygon : allOperators;

    if (listMode === "visible") {
      // Show only operators in current viewport
      setResults(operatorsInView, operatorsInView.length);
    } else {
      // Show all operators (filtered)
      setResults(baseOperators, baseOperators.length);
    }
  }, [allOperators, drawnPolygon, filters.searchText, filteredByPolygon, operatorsInView, listMode, setResults]);

  return {
    clusters,
    allOperators,
    filteredOperators,
    operatorsInView,
    totalCount: allOperators.length,
    totalInView,
    isLoading,
    isError,
    getClusterExpansionZoom,
    getClusterLeaves,
    hasPolygon: !!drawnPolygon,
  };
}

/**
 * Ray-casting algorithm for point-in-polygon test
 */
function isPointInPolygon(point: [number, number], polygon: GeoJSON.Polygon): boolean {
  const [x, y] = point;
  const coords = polygon.coordinates[0];
  let inside = false;

  for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
    const xi = coords[i][0];
    const yi = coords[i][1];
    const xj = coords[j][0];
    const yj = coords[j][1];

    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }

  return inside;
}
